import React, { useMemo } from 'react';
import { Box, Chip, Paper, Stack, Typography, useTheme } from '@mui/material';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { BOLT_SCALE, boltRating } from '../../data/apneaProtocols';
import { BOLT_DECLINE_DAYS } from '../../utils/apneaRules';

/**
 * BOLT trend.
 *
 * BOLT is the daily signal the program leans on — quick, repeatable and
 * essentially risk-free, unlike a max static. Three consecutive declines is the
 * earliest overtraining warning available, so the chart marks that explicitly
 * rather than leaving the user to eyeball it.
 */

export interface BoltPoint {
  date: string;
  seconds: number;
  /** Hand-entered readings are marked, since they are less precise evidence. */
  manual?: boolean;
}

interface BoltTrendChartProps {
  points: BoltPoint[];
}

const BoltTrendChart: React.FC<BoltTrendChartProps> = ({ points }) => {
  const theme = useTheme();

  const data = useMemo(
    () => [...points]
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .map(p => ({
        ...p,
        label: new Date(p.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
      })),
    [points]
  );

  // Trailing consecutive declines, which is what the back-off rule keys on.
  const decliningStreak = useMemo(() => {
    let streak = 0;
    for (let i = data.length - 1; i > 0; i -= 1) {
      if (data[i].seconds < data[i - 1].seconds) streak += 1;
      else break;
    }
    return streak;
  }, [data]);

  if (data.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="subtitle1" gutterBottom>BOLT trend</Typography>
        <Typography variant="body2" color="text.secondary">
          No BOLT readings yet. Measure on waking, before getting out of bed, and stop at
          the first definite urge to breathe.
        </Typography>
      </Paper>
    );
  }

  const latest = data[data.length - 1];
  const maxSeconds = Math.max(45, ...data.map(d => d.seconds));

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}
      >
        <Typography variant="subtitle1">BOLT trend</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip size="small" label={`latest ${latest.seconds}s · ${boltRating(latest.seconds)}`} />
          {decliningStreak >= BOLT_DECLINE_DAYS && (
            <Chip
              size="small"
              color="warning"
              label={`down ${decliningStreak} readings — take two easy days`}
            />
          )}
        </Stack>
      </Stack>

      {decliningStreak >= BOLT_DECLINE_DAYS && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {BOLT_DECLINE_DAYS}+ consecutive lower readings is the earliest overtraining signal
          this app tracks — it usually reflects poor sleep, stress or under-recovery before it
          shows up anywhere else.
        </Typography>
      )}

      <Box sx={{ height: 220 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
            {/* Buteyko bands, so a number has meaning without a legend lookup. */}
            <ReferenceArea y1={0} y2={10} fill={theme.palette.error.main} fillOpacity={0.06} />
            <ReferenceArea y1={10} y2={25} fill={theme.palette.warning.main} fillOpacity={0.06} />
            <ReferenceArea y1={25} y2={maxSeconds} fill={theme.palette.success.main} fillOpacity={0.06} />
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis domain={[0, maxSeconds]} tick={{ fontSize: 11 }} unit="s" />
            <Tooltip
              formatter={(value: number, _name, entry) => [
                `${value}s · ${boltRating(value)}${
                  (entry?.payload as BoltPoint | undefined)?.manual ? ' (entered by hand)' : ''
                }`,
                'BOLT',
              ]}
            />
            <Line
              type="monotone"
              dataKey="seconds"
              stroke={theme.palette.primary.main}
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>

      <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
        {BOLT_SCALE.map(band => (
          <Typography key={band.label} variant="caption" color="text.secondary">
            {band.label}
            {band.maxSeconds === null ? ' ≥40s' : ` ≤${band.maxSeconds}s`}
          </Typography>
        ))}
      </Stack>
    </Paper>
  );
};

export default BoltTrendChart;
